import type { Cell, Borders, Worksheet } from "exceljs";

/**
 * Bộ dựng ô dùng chung cho các chứng từ xuất ra Excel.
 *
 * Trước 15/09/2026 mỗi tệp xuất tự chép lại cùng một đoạn: bảng màu, viền, hàm
 * định dạng ô, hàm gộp ô, và hàm ước lượng chiều cao dòng. PI, báo giá và hoá
 * đơn đã ba bản; thêm phiếu đóng gói và phiếu giao hàng là năm. Năm bản của
 * cùng một đoạn là năm chỗ để lệch nhau, và chúng lệch thật — xem
 * `documentTotals.ts` về chuyện làm tròn.
 *
 * Ba tệp cũ CHƯA chuyển sang đây: chúng đang chạy, đã có bài kiểm, và đổi chúng
 * chỉ để gọn thì đặt rủi ro lên thứ đang phục vụ khách. Chuyển khi nào có lý do
 * khác để động vào chúng.
 */

// Màu và nhãn của chứng từ thương mại tiếng Anh, KHÔNG phải token giao diện.
export const INK = "FF000000";
export const WHITE = "FFFFFFFF";
export const GREY = "FFF5F5F5";
export const WARN_TINT = "FFFFF4E5";

export const THIN_BORDER: Partial<Borders> = Object.fromEntries(
  ["top", "bottom", "left", "right"].map((side) => [side, { style: "thin", color: { argb: INK } }]),
);

/** Chuỗi rỗng hoặc toàn khoảng trắng coi như không có. */
export const text = (v: string | null | undefined, fallback = "N/A") => v?.trim() || fallback;

/**
 * Ước lượng chiều cao một dòng theo nội dung.
 *
 * Ký tự CJK rộng gần gấp đôi chữ Latin — mô tả sản phẩm ở đây thường xuyên là
 * tiếng Trung, nên đếm theo số ký tự sẽ ra dòng quá thấp và chữ bị cắt.
 */
export const wrappedHeight = (value: string, characters: number, font = 10) =>
  Math.max(
    18,
    value.split("\n").reduce(
      (a, line) => a + Math.max(1, Math.ceil(
        Array.from(line).reduce((k, ch) => k + (ch.charCodeAt(0) > 255 ? 1.7 : 1), 0) / Math.max(3, characters),
      )),
      0,
    ) * (font * 1.3) + 7,
  );

export function sheetWriter(sheet: Worksheet) {
  const format = (cell: Cell, bold = false, size = 10) => {
    cell.font = { name: "Arial", size, bold, color: { argb: INK } };
    cell.alignment = { vertical: "top", wrapText: true };
    return cell;
  };
  const merged = (r: number, c: number, r2: number, c2: number, value: string | number, bold = false, size = 10) => {
    if (r !== r2 || c !== c2) sheet.mergeCells(r, c, r2, c2);
    const cell = sheet.getCell(r, c);
    cell.value = value;
    return format(cell, bold, size);
  };
  /** Hàng tiêu đề bảng: nền đen chữ trắng, giống hệt bản in. */
  const headerRow = (row: number, labels: readonly string[], size = 9) => {
    labels.forEach((label, i) => {
      const cell = sheet.getCell(row, i + 1);
      cell.value = label;
      format(cell, true, size);
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: INK } };
      cell.font = { ...cell.font, color: { argb: WHITE } };
      cell.border = THIN_BORDER;
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    });
    sheet.getRow(row).height = 32;
    sheet.pageSetup.printTitlesRow = `${row}:${row}`;
  };
  return { format, merged, headerRow };
}

/** Kích hoạt tải tệp .xlsx về máy. */
export async function downloadWorkbook(book: { xlsx: { writeBuffer: () => Promise<ArrayBuffer> } }, fileName: string) {
  const buffer = await book.xlsx.writeBuffer();
  const url = URL.createObjectURL(
    new Blob([new Uint8Array(buffer)], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Tên tệp an toàn cho mọi hệ điều hành.
 *
 * Số chứng từ do người dùng nhập nên có thể chứa `/` — trên macOS và Linux đó
 * là dấu phân cấp thư mục, tệp tải về rơi vào chỗ không ai tìm ra.
 */
// eslint-disable-next-line no-control-regex -- CỐ Ý: lọc ký tự điều khiển ra khỏi tên tệp.
export const safeFileName = (prefix: string, no: string) => `${prefix}-${no.replace(/[\\/:*?"<>|\u0000-\u001f]/g, "_")}`;
